import { type ReactNode, useState } from 'react';
import { BarSegmentPopover } from './bar-segment-popover';

export type BarSegment = {
  id: string;
  label: string;
  value: number;
  color: string;
  photo?: string;
  role?: string;
};

function glow(color: string): string {
  const mix = (a: number): string =>
    `color-mix(in srgb, ${color} ${a}%, transparent)`;
  return `0 0 12px ${mix(30)}, 0 0 6px ${mix(20)}`;
}

function segStyle(pct: number, color: string): React.CSSProperties {
  return {
    width: `${pct}%`,
    backgroundColor: color,
    transition: 'width 0.5s',
    boxShadow: glow(color),
  };
}

function Particles({ color }: { color: string }): ReactNode {
  const dots = [12, 38, 65, 85];
  return (
    <>
      {dots.map((left, i) => (
        <span
          key={i}
          className="absolute h-[2px] w-[2px] rounded-full opacity-70"
          style={
            {
              left: `${left}%`,
              top: '20%',
              backgroundColor: color,
              animation: `particle-emit ${2.4 + i * 0.3}s ${i * 0.5}s ease-out infinite`,
            } as React.CSSProperties
          }
        />
      ))}
    </>
  );
}

export function BarSegmentView({
  seg,
  pct,
  barGoal,
}: {
  seg: BarSegment;
  pct: number;
  barGoal: number;
}): ReactNode {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="relative h-full overflow-visible first:rounded-l-lg last:rounded-r-lg"
      style={segStyle(pct, seg.color)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {pct > 2 && <Particles color={seg.color} />}
      <BarSegmentPopover seg={seg} barGoal={barGoal} visible={hover} />
    </div>
  );
}
